import React from 'react';
import LeftSidebar from './components/LeftSidebar';
import ExplorerToolbar from './explorer/ExplorerToolbar';
import ExplorerBody from './explorer/ExplorerBody';
import DetailPane from './components/DetailPane';
import MobilePreviewModal from './components/MobilePreviewModal';
import { FOLDERS, FILES, PROFILE_DATA, BIO, ABOUT, SKILLS, CASE_STUDIES } from './profileConstants';
import useTimeInIST from './hooks/useTimeInIST';
import useExplorerState from './hooks/useExplorerState';
import { ThemeProvider, useTheme } from './context/ThemeContext';

const ProfileLandingContent = ({ onOpenDesktop, onOpenCaseStudy, onOpen3DExperience }) => {
  const { currentTime, currentDate } = useTimeInIST();
  const {
    selectedFolder,
    selectedSubfolder,
    selectedFile,
    selectedCaseStudy,
    isModalOpen,
    viewMode,
    selectedIconItem,
    iconViewPath,
    handleFolderClick,
    handleSubfolderClick,
    handleFileClick,
    handleCaseStudyClick,
    handleCloseModal,
    handleIconItemDoubleClick,
    handleIconItemClick,
    handleBreadcrumbClick,
    setViewMode
  } = useExplorerState();
  const { colors, theme, toggleTheme } = useTheme();

  return (
    <div className="h-screen w-screen flex flex-col lg:flex-row lg:justify-center overflow-hidden relative" style={{ fontFamily: "'PP Neue Bit', monospace", backgroundColor: colors.background, color: colors.textPrimary }}>
      {/* Bottom horizontal dashed border - Desktop only */}
      <div
        className="hidden lg:block absolute bottom-[50px] left-4 right-4 h-0 z-10"
        style={{
          borderBottom: `1px dashed ${colors.border}`,
          borderImageSource: `repeating-linear-gradient(to right, ${colors.border} 0, ${colors.border} 2px, transparent 2px, transparent 6px)`,
          borderImageSlice: 1
        }}
      ></div>
      {/* Left Sidebar */}
      <LeftSidebar
        currentTime={currentTime}
        currentDate={currentDate}
        profileData={PROFILE_DATA}
        bio={BIO}
      />
      {/* Main Content Area */}
      <div className="flex-1 lg:flex-none flex flex-col lg:flex-row" style={{ maxHeight: 'calc(100vh - 50px)', overflow: 'hidden' }}>
        {/* File Explorer */}
        <div className="w-full lg:w-[552px] pl-2 sm:pl-3 lg:pl-4 pr-2 sm:pr-3 lg:pr-4 pt-4 relative flex flex-col" style={{ height: '100%', maxHeight: '100%' }}>
          {/* Top horizontal dashed border with 16px gap from edges */}
          <div
            className="absolute top-4 left-4 right-4 h-0"
            style={{
              borderTop: `1px dashed ${colors.border}`,
              borderImageSource: `repeating-linear-gradient(to right, ${colors.border} 0, ${colors.border} 2px, transparent 2px, transparent 6px)`,
              borderImageSlice: 1
            }}
          ></div>

          {/* Right vertical dashed border - Desktop only */}
          <div
            className="hidden lg:block absolute top-4 right-0 bottom-[16px] w-0"
            style={{
              borderRight: `1px dashed ${colors.border}`,
              borderImageSource: `repeating-linear-gradient(to bottom, ${colors.border} 0, ${colors.border} 2px, transparent 2px, transparent 6px)`,
              borderImageSlice: 1
            }}
          ></div>

          {/* Toolbar - Desktop only */}
          <ExplorerToolbar
            viewMode={viewMode}
            breadcrumb={iconViewPath}
            onViewModeChange={setViewMode}
            onBreadcrumbClick={handleBreadcrumbClick}
          />

          {/* Content Area - Scrollable */}
          <div className="mt-4 pl-2 sm:pl-3 lg:pl-4 pr-2 sm:pr-3 lg:pr-4 flex-1 overflow-auto relative" style={{ scrollbarWidth: 'thin', scrollbarColor: `${colors.border} ${colors.background}` }}>
            <ExplorerBody
              viewMode={viewMode}
              selectedFolder={selectedFolder}
              selectedSubfolder={selectedSubfolder}
              iconViewPath={iconViewPath}
              selectedIconItem={selectedIconItem}
              handleFolderClick={handleFolderClick}
              handleSubfolderClick={handleSubfolderClick}
              handleFileClick={handleFileClick}
              handleCaseStudyClick={handleCaseStudyClick}
              handleIconItemDoubleClick={handleIconItemDoubleClick}
              handleIconItemClick={handleIconItemClick}
              handleBreadcrumbClick={handleBreadcrumbClick}
              FOLDERS={FOLDERS}
              FILES={FILES}
              CASE_STUDIES={CASE_STUDIES}
              selectedFile={selectedFile}
              selectedCaseStudy={selectedCaseStudy}
            />
          </div>
        </div>

        {/* Right Content Area - Merged Column (Desktop Only) */}
        <div className="hidden lg:block w-[508px] overflow-hidden" style={{ height: 'calc(100vh - 66px)', maxHeight: 'calc(100vh - 66px)' }}>
          {/* Main Content Area */}
          <div className="h-full overflow-y-auto overflow-x-hidden" style={{ scrollbarWidth: 'thin', scrollbarColor: `${colors.border} ${colors.background}` }}>
            <DetailPane
              selectedFile={selectedFile}
              selectedCaseStudy={selectedCaseStudy}
              ABOUT={ABOUT}
              SKILLS={SKILLS}
              CASE_STUDIES={CASE_STUDIES}
              PROFILE_DATA={PROFILE_DATA}
            />
          </div>
        </div>
      </div>

      {/* Preview Modal for Mobile/Tablet */}
      <MobilePreviewModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        selectedFile={selectedFile}
        selectedCaseStudy={selectedCaseStudy}
        ABOUT={ABOUT}
        SKILLS={SKILLS}
        CASE_STUDIES={CASE_STUDIES}
        PROFILE_DATA={PROFILE_DATA}
      />

      {/* Footer - Theme & Socials - Desktop Only */}
      <div className="hidden lg:flex absolute bottom-0 h-[50px] justify-between items-center z-20 left-1/2 -translate-x-1/2 w-full max-w-[1512px] px-4">
        <div className="flex gap-4 text-[20px]">
          <button
            onClick={() => toggleTheme('dark')}
            className="hover:opacity-80 transition-opacity cursor-pointer"
            style={{ color: theme === 'dark' ? colors.textPrimary : colors.textSecondary }}
          >
            [dark]
          </button>
          <button
            onClick={() => toggleTheme('light')}
            className="hover:opacity-80 transition-opacity cursor-pointer"
            style={{ color: theme === 'light' ? colors.textPrimary : colors.textSecondary }}
          >
            [light]
          </button>
        </div>
        <div className="flex gap-4 text-[20px]">
          <a
            href="https://linkedin.com/in/vimarshtiwari"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:opacity-80 transition-opacity cursor-pointer"
            style={{ color: colors.textSecondary }}
          >
            [linkedin]
          </a>
          <a
            href="https://x.com/vimarshx"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:opacity-80 transition-opacity cursor-pointer"
            style={{ color: colors.textSecondary }}
          >
            [x]
          </a>
        </div>
      </div>
    </div>
  );
};

const ProfileLanding = (props) => (
  <ThemeProvider>
    <ProfileLandingContent {...props} />
  </ThemeProvider>
);

export default ProfileLanding;
